// The WORK BOARD surface (brief §5.2) — one Workflow Definition's live items on its declared axis.
// Thin over services: it fetches the board projection through the injected WorkApi and renders it
// natively (tokens only, G-181) — columns are EXACTLY the projection's lifecycle order with unknown
// keys last (G-182), counts stay whole-board, cards carry NO verdict control (verdicts live on the
// card page, brief §5.2/§3.5), and the honest states (pre-first-sweep, sweep-failed, over-cap,
// unavailable) all derive from the projection. Navigation is CALLBACKS ONLY (surface-contracts);
// Run-now is an optional container-wired callback — the WorkApi carries no sweep trigger this run.
// The v1 filters narrow the slice client-side; the surface never edits, approves, or fabricates.

import { useCallback, useState } from 'react';
import { useWorkApi } from '../api-client.ts';
import { useProjection } from '../../useProjection.ts';
import { SurfaceFrame } from '../../surface-frame.tsx';
import type { WorkBoardProjection } from '../projections.ts';
import type { WorkBoardSurfaceProps } from '../surface-contracts.ts';
import {
  applyBoardFilter,
  boardTagOptions,
  EMPTY_BOARD_FILTER,
  orderedBoardStates,
  resolveAxis,
  type BoardAxis,
  type BoardFilterState,
} from './board-model.ts';
import { BoardColumns } from './components/BoardColumns.tsx';
import { BoardFilterBar } from './components/BoardFilterBar.tsx';
import { BoardList } from './components/BoardList.tsx';
import { BoardNotices } from './components/BoardNotices.tsx';
import { BoardStats } from './components/BoardStats.tsx';
import { BoardToolbar } from './components/BoardToolbar.tsx';
import { CalendarView } from './components/CalendarView.tsx';
import { TimelineView } from './components/TimelineView.tsx';

/** The contract props plus the optional Run-now callback the container may wire (no dead control). */
export interface BoardSurfaceProps extends WorkBoardSurfaceProps {
  readonly onRunNow?: () => void;
}

/** Render the chosen axis over the filtered slice — the same rows and the same onOpenCard everywhere
 *  (views are arrangements only). Calendar/timeline read the board's asOfDate for their deterministic
 *  today; kanban keeps the derived column order. */
function BoardAxisBody({
  board,
  axis,
  states,
  visible,
  onOpenCard,
}: {
  readonly board: WorkBoardProjection;
  readonly axis: BoardAxis;
  readonly states: readonly string[];
  readonly visible: readonly WorkBoardProjection['items'][number][];
  readonly onOpenCard: (itemId: string) => void;
}): React.JSX.Element {
  if (axis === 'calendar')
    return <CalendarView board={board} items={visible} onOpenCard={onOpenCard} />;
  if (axis === 'timeline')
    return <TimelineView board={board} items={visible} onOpenCard={onOpenCard} />;
  if (axis === 'list') return <BoardList board={board} items={visible} onOpenCard={onOpenCard} />;
  return <BoardColumns board={board} states={states} items={visible} onOpenCard={onOpenCard} />;
}

/** One definition's board: toolbar, stats, honest notices, filters, then the chosen axis. */
export function WorkBoardSurface({
  definitionId,
  onOpenCard,
  onBack,
  onRunNow,
}: BoardSurfaceProps): React.JSX.Element {
  const api = useWorkApi();
  const loadBoard = useCallback(() => api.getBoard(definitionId), [api, definitionId]);
  const state = useProjection<WorkBoardProjection>(loadBoard);
  const [axisOverride, setAxisOverride] = useState<BoardAxis | null>(null);
  const [filter, setFilter] = useState<BoardFilterState>(EMPTY_BOARD_FILTER);

  return (
    <SurfaceFrame state={state}>
      {(board) => {
        const axis = resolveAxis(board, axisOverride);
        const states = orderedBoardStates(board);
        const visible = applyBoardFilter(board.items, filter);
        return (
          <div>
            <BoardToolbar
              definitionName={board.definitionName}
              lastSweep={board.lastSweep}
              nextDueAt={board.nextDueAt}
              axis={axis}
              availableViews={board.availableViews}
              onAxisChange={setAxisOverride}
              onBack={onBack}
              onRunNow={onRunNow}
            />
            <BoardStats board={board} />
            <BoardNotices board={board} />
            <BoardFilterBar
              states={states}
              tags={boardTagOptions(board.items)}
              filter={filter}
              onFilterChange={setFilter}
            />
            <BoardAxisBody
              board={board}
              axis={axis}
              states={states}
              visible={visible}
              onOpenCard={onOpenCard}
            />
          </div>
        );
      }}
    </SurfaceFrame>
  );
}
